import { useEffect, useRef } from 'react';

export interface UseNetworkStatusHandlers {
  /** Called when the browser fires the 'online' event. */
  onOnline?: () => void;
  /** Called when the browser fires the 'offline' event. */
  onOffline?: () => void;
}

/**
 * Subscribe to the browser's online/offline events. The latest handlers are
 * always invoked (no stale closures) and the listeners are attached once for
 * the lifetime of the consuming component. Used for instant WebSocket
 * reconnection instead of waiting for the browser's ~30s TCP timeout to detect
 * a dead socket.
 */
export const useNetworkStatus = ({ onOnline, onOffline }: UseNetworkStatusHandlers): void => {
  const onOnlineRef = useRef(onOnline);
  const onOfflineRef = useRef(onOffline);
  onOnlineRef.current = onOnline;
  onOfflineRef.current = onOffline;

  useEffect(() => {
    const handleOnline = () => onOnlineRef.current?.();
    const handleOffline = () => onOfflineRef.current?.();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
};
